import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
    Play,
    Pause,
    RotateCcw,
    RotateCw,
    Volume2,
    VolumeX,
    ChevronDown
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AudioPlayerProps {
    url: string;
    className?: string;
}

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export function AudioPlayer({ url, className }: AudioPlayerProps) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const setAudioData = () => {
            setDuration(audio.duration);
            setCurrentTime(audio.currentTime);
        };

        const setAudioTime = () => setCurrentTime(audio.currentTime);
        
        audio.addEventListener('loadeddata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);
        
        return () => {
            // Pause and reset the audio when component unmounts
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
                audio.src = '';
            }
            audio.removeEventListener('loadeddata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
        };
    }, []);

    const formatTime = (time: number) => {
        if (isNaN(time)) return '0:00';
        
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const togglePlay = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const skipForward = () => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = Math.min(audioRef.current.currentTime + 5, duration);
    };

    const skipBackward = () => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 5, 0);
    };

    const toggleMute = () => {
        if (!audioRef.current) return;
        
        if (isMuted) {
            audioRef.current.volume = volume;
            setIsMuted(false);
        } else {
            audioRef.current.volume = 0;
            setIsMuted(true);
        }
    };

    const handleVolumeChange = (value: number[]) => {
        const newVolume = value[0];
        if (!audioRef.current) return;
        
        audioRef.current.volume = newVolume;
        setVolume(newVolume);
        if (newVolume === 0) {
            setIsMuted(true);
        } else if (isMuted) {
            setIsMuted(false);
        }
    };

    const onSliderChange = (value: number[]) => {
        if (!audioRef.current) return;
        const time = value[0];
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const handleSpeedChange = (speed: number) => {
        if (!audioRef.current) return;
        audioRef.current.playbackRate = speed;
        setPlaybackSpeed(speed);
    };

    return (
        <div className={cn("w-full flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-4 p-2 md:h-12 relative", className)}>
            <audio
                ref={audioRef}
                src={url}
                onEnded={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            />
            
            <div className="flex items-center gap-2 order-1 md:order-1">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={skipBackward}
                    title="Back 5 seconds"
                    className="h-8 w-8"
                >
                    <RotateCcw className="h-4 w-4" />
                </Button>

                <Button
                    variant="outline"
                    size="icon"
                    onClick={togglePlay}
                    className="h-8 w-8"
                >
                    {isPlaying ? (
                        <Pause className="h-4 w-4" />
                    ) : (
                        <Play className="h-4 w-4" />
                    )}
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={skipForward}
                    title="Forward 5 seconds"
                    className="h-8 w-8"
                >
                    <RotateCw className="h-4 w-4" />
                </Button>
            </div>

            {/* Speed dropdown for mobile - positioned absolutely in top right */}
            <div className="md:hidden absolute top-2 right-2">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button 
                            variant="outline" 
                            size="sm"
                            className="flex items-center gap-1 h-7 px-2"
                        >
                            {playbackSpeed}x
                            <ChevronDown className="h-3 w-3 opacity-50" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {PLAYBACK_SPEEDS.map(speed => (
                            <DropdownMenuItem
                                key={speed}
                                onClick={() => handleSpeedChange(speed)}
                                className={cn(
                                    "flex items-center justify-between",
                                    speed === playbackSpeed && "font-medium"
                                )}
                            >
                                {speed}x
                                {speed === playbackSpeed && (
                                    <div className="w-2 h-2 bg-primary rounded-full" />
                                )}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <div className="flex w-full md:flex-1 items-center gap-2 order-4 md:order-2">
                <div className="flex items-center min-w-[50px]">
                    <span className="text-sm tabular-nums text-muted-foreground">
                        {formatTime(currentTime)}
                    </span>
                </div>

                <div className="flex-1">
                    <Slider
                        value={[currentTime]}
                        max={duration || 100}
                        step={1}
                        onValueChange={onSliderChange}
                    />
                </div>

                <div className="flex items-center min-w-[50px]">
                    <span className="text-sm tabular-nums text-muted-foreground">
                        {formatTime(duration)}
                    </span>
                </div>
            </div>

            <div className="hidden md:flex items-center gap-2 order-5 md:order-3">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleMute}
                    className="h-8 w-8"
                >
                    {isMuted ? (
                        <VolumeX className="h-4 w-4" />
                    ) : (
                        <Volume2 className="h-4 w-4" />
                    )}
                </Button>
                <Slider
                    value={[isMuted ? 0 : volume]}
                    max={1}
                    step={0.1}
                    onValueChange={handleVolumeChange}
                    className="w-24"
                />
            </div>

            {/* Speed dropdown for desktop */}
            <div className="hidden md:block order-6 md:order-4">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button 
                            variant="outline" 
                            size="sm"
                            className="flex items-center gap-1 h-8"
                        >
                            {playbackSpeed}x
                            <ChevronDown className="h-3 w-3 opacity-50" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {PLAYBACK_SPEEDS.map(speed => (
                            <DropdownMenuItem
                                key={speed}
                                onClick={() => handleSpeedChange(speed)}
                                className={cn(
                                    "flex items-center justify-between",
                                    speed === playbackSpeed && "font-medium"
                                )}
                            >
                                {speed}x
                                {speed === playbackSpeed && (
                                    <div className="w-2 h-2 bg-primary rounded-full" />
                                )}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
